import { useState, useCallback, useRef, useEffect } from 'react';
import type { ChatMessage } from '@/types/chat';

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`;

function getSessionId(): string {
  const key = 'qlivar_chat_session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
}

function resetSessionId(): void {
  sessionStorage.removeItem('qlivar_chat_session');
}

export interface PublicChatOptions {
  visitorName?: string;
  visitorEmail?: string;
  /** Detected UI language — sent to the edge function so it can return bilingual system messages */
  language?: 'pt' | 'en';
  /** Minutes of inactivity before session auto-closes. 0 = disabled. Default: 30 */
  inactivityTimeoutMinutes?: number;
}

export function usePublicChat(agentId: string | null, options: PublicChatOptions = {}) {
  const { visitorName, visitorEmail, language = 'pt', inactivityTimeoutMinutes = 30 } = options;

  const [messages, setMessages] = useState<Pick<ChatMessage, 'role' | 'content'>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const addAssistantMessage = useCallback((content: string) => {
    setMessages(prev => [...prev, { role: 'assistant' as const, content }]);
  }, []);

  const lockWithMessage = useCallback((msg: string) => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    setIsLocked(true);
    setMessages(prev => [...prev, { role: 'assistant' as const, content: msg }]);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (!inactivityTimeoutMinutes || inactivityTimeoutMinutes <= 0) return;
    inactivityTimerRef.current = setTimeout(() => {
      const msg = language === 'en'
        ? 'This conversation was closed due to inactivity. Thank you for reaching out! To continue, start a new conversation.'
        : 'Esta conversa foi encerrada por inatividade. Obrigado pelo contato! Para continuar, inicie uma nova conversa.';
      lockWithMessage(msg);
    }, inactivityTimeoutMinutes * 60 * 1000);
  }, [inactivityTimeoutMinutes, lockWithMessage]);

  // Reset on agent change
  useEffect(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    setIsLocked(false);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
  }, [agentId]);

  // Cleanup inactivity timer on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    if (!agentId || !text.trim() || isLoading || isLocked) return;

    setError(null);
    resetInactivityTimer();
    const userMsg = { role: 'user' as const, content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          agent_id: agentId,
          conversation_id: conversationId,
          visitor_session_id: getSessionId(),
          visitor_name: visitorName || undefined,
          visitor_email: visitorEmail || undefined,
          visitor_language: language,
          message: text.trim(),
        }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({ error: 'Connection error' }));
        // Rate limited — show as assistant bubble, don't lock
        if (resp.status === 429 && errData.friendly_message) {
          addAssistantMessage(errData.friendly_message);
          setIsLoading(false);
          return;
        }
        // Conversation ended (message limit) — show as assistant bubble and lock
        if (resp.status === 410 && errData.friendly_message) {
          lockWithMessage(errData.friendly_message);
          setIsLoading(false);
          return;
        }
        setError(errData.error || 'Something went wrong');
        setIsLoading(false);
        return;
      }

      if (!resp.body) {
        setError('No response body');
        setIsLoading(false);
        return;
      }

      // Stream response
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantSoFar = '';

      const upsertAssistant = (chunk: string) => {
        assistantSoFar += chunk;
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
          }
          return [...prev, { role: 'assistant', content: assistantSoFar }];
        });
      };

      let streamDone = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done || streamDone) break;
        buffer += decoder.decode(value, { stream: true });

        let newlineIdx: number;
        while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIdx);
          buffer = buffer.slice(newlineIdx + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') { streamDone = true; break; }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) upsertAssistant(content);
          } catch {
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      // Flush remaining
      if (buffer.trim()) {
        for (let raw of buffer.split('\n')) {
          if (!raw) continue;
          if (raw.endsWith('\r')) raw = raw.slice(0, -1);
          if (raw.startsWith(':') || raw.trim() === '') continue;
          if (!raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) upsertAssistant(content);
          } catch { /* skip */ }
        }
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        setError('Failed to send message');
      }
    } finally {
      setIsLoading(false);
      abortRef.current = null;
    }
  }, [agentId, conversationId, isLoading, isLocked, visitorName, visitorEmail, language,
      resetInactivityTimer, addAssistantMessage, lockWithMessage]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setIsLoading(false);
  }, []);

  const startNewConversation = useCallback(() => {
    resetSessionId();
    setMessages([]);
    setConversationId(null);
    setError(null);
    setIsLocked(false);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
  }, []);

  return { messages, isLoading, error, isLocked, sendMessage, cancel, startNewConversation };
}
